import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  usePendingMutation,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type StreamAudioFeed = {
  id: string;
  projectID: string;
  name: string;
  sortOrder: number;
};

export type StreamVideoFeed = {
  id: string;
  projectID: string;
  name: string;
  description: string;
  sortOrder: number;
};

export const useStreamAudioFeeds = (projectId: string | undefined) => {
  const query = projectId
    ? appQueries.streamAudioFeeds.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const feeds = (data ?? []) as StreamAudioFeed[];
  return {
    data: feeds,
    isLoading: !!projectId && details.type !== "complete" && feeds.length === 0,
  };
};

export const useCreateStreamAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: { projectID: string; name: string; sortOrder?: number },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamAudioFeeds.create({
            id: generateUUID(),
            projectID: input.projectID,
            name: input.name,
            sortOrder: input.sortOrder ?? 0,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Audio feed create was rejected",
        );
      })
        .then(() => {
          toast.success("Audio feed added");
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to add audio feed: " + err.message),
        );
    },
    isPending,
  };
};

export const useUpdateStreamAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: {
        id: string;
        projectID: string;
        name?: string;
        sortOrder?: number;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamAudioFeeds.update(input),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Audio feed update was rejected",
        );
      })
        .then(() => {
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to update audio feed: " + err.message),
        );
    },
    isPending,
  };
};

export const useDeleteStreamAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamAudioFeeds.delete({
            id: input.id,
            projectID: input.projectId,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Audio feed delete was rejected",
        );
      })
        .then(() => {
          toast.success("Audio feed removed");
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to remove audio feed: " + err.message),
        );
    },
    isPending,
  };
};

export const useStreamVideoFeeds = (projectId: string | undefined) => {
  const query = projectId
    ? appQueries.streamVideoFeeds.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const feeds = (data ?? []) as StreamVideoFeed[];
  return {
    data: feeds,
    isLoading: !!projectId && details.type !== "complete" && feeds.length === 0,
  };
};

export const useCreateStreamVideoFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: {
        projectID: string;
        name: string;
        description?: string;
        sortOrder?: number;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamVideoFeeds.create({
            id: generateUUID(),
            projectID: input.projectID,
            name: input.name,
            description: input.description ?? "",
            sortOrder: input.sortOrder ?? 0,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Video feed create was rejected",
        );
      })
        .then(() => {
          toast.success("Video feed added");
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to add video feed: " + err.message),
        );
    },
    isPending,
  };
};

export const useUpdateStreamVideoFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: {
        id: string;
        projectID: string;
        name?: string;
        description?: string;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamVideoFeeds.update(input),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Video feed update was rejected",
        );
      })
        .then(() => {
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to update video feed: " + err.message),
        );
    },
    isPending,
  };
};

export const useDeleteStreamVideoFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamVideoFeeds.delete({
            id: input.id,
            projectID: input.projectId,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Video feed delete was rejected",
        );
      })
        .then(() => {
          toast.success("Video feed removed");
          options?.onSuccess?.();
        })
        .catch((err) =>
          toast.error("Failed to remove video feed: " + err.message),
        );
    },
    isPending,
  };
};
